import * as THREE from 'three';
import { VOXEL_SIZE } from './VoxelInstanceBuffer';

export class SmokeParticleSystem {
  private particles: {
    position: THREE.Vector3;
    startY: number;
    scale: number;
    speedY: number;
    maxLife: number;
    life: number;
    driftAngle: number;
  }[] = [];
  private instancedMesh: THREE.InstancedMesh;
  private maxParticles = 90;
  private dummy = new THREE.Object3D();

  constructor(scene: THREE.Scene) {
    const geometry = new THREE.BoxGeometry(VOXEL_SIZE * 0.85, VOXEL_SIZE * 0.85, VOXEL_SIZE * 0.85);
    const material = new THREE.MeshStandardMaterial({
      color: 0xf5f7fa,
      roughness: 0.9,
      transparent: true,
      opacity: 0.70,
    });

    this.instancedMesh = new THREE.InstancedMesh(geometry, material, this.maxParticles);
    this.instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.instancedMesh.castShadow = false;
    this.instancedMesh.receiveShadow = false;
    // Same cached-bounding-sphere trap as PetalSystem: the puffs start at y = -999, so the mesh
    // would be frustum-culled for the lifetime of the scene.
    this.instancedMesh.frustumCulled = false;

    // Initialize offscreen
    for (let i = 0; i < this.maxParticles; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.instancedMesh.instanceMatrix.needsUpdate = true;

    scene.add(this.instancedMesh);
  }

  public update(chimneyPositions: { x: number; y: number; z: number }[], delta: number) {
    // Spawn new soft smoke puffs at chimneys
    if (this.particles.length < this.maxParticles && chimneyPositions.length > 0 && Math.random() < 0.35) {
      const source = chimneyPositions[Math.floor(Math.random() * chimneyPositions.length)]!;
      const startY = (source.y + 0.5) * VOXEL_SIZE;

      this.particles.push({
        position: new THREE.Vector3(
          (source.x + (Math.random() - 0.5) * 0.2) * VOXEL_SIZE,
          startY,
          (source.z + (Math.random() - 0.5) * 0.2) * VOXEL_SIZE
        ),
        startY,
        scale: 0.6 + Math.random() * 0.3,
        speedY: (1.0 + Math.random() * 0.5) * VOXEL_SIZE,
        maxLife: 2.2 + Math.random() * 1.0,
        life: 0,
        driftAngle: Math.random() * Math.PI * 2
      });
    }

    // Update active smoke puffs
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.life += delta;

      const progress = p.life / p.maxLife;

      p.position.y += p.speedY * delta;
      p.position.x += Math.sin(p.life * 2.2 + p.driftAngle) * 0.25 * VOXEL_SIZE * delta;
      p.position.z += Math.cos(p.life * 1.8 + p.driftAngle) * 0.25 * VOXEL_SIZE * delta;

      // Expand gently as it rises
      const currentScale = p.scale * (1.0 + progress * 1.1);

      if (progress >= 1.0 || p.position.y > (p.startY + 4.5 * VOXEL_SIZE)) {
        this.particles.splice(i, 1);
        continue;
      }

      this.dummy.position.copy(p.position);
      this.dummy.scale.set(currentScale, currentScale, currentScale);
      this.dummy.updateMatrix();
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);
    }

    // Hide unused instances
    for (let i = this.particles.length; i < this.maxParticles; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);
    }

    this.instancedMesh.instanceMatrix.needsUpdate = true;
  }

  public destroy(scene: THREE.Scene) {
    scene.remove(this.instancedMesh);
    this.instancedMesh.geometry.dispose();
    (this.instancedMesh.material as THREE.Material).dispose();
  }
}
